import { Delete02Icon, DeletePutBackIcon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ScrollArea,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  useSidebar,
} from "@motleyhand/design"
import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { CONFIG } from "./definitionMenuConfig"
import {
  useGetTrashedDefinitionsQuery,
  useRestoreDefinitionMutation,
} from "@/common"
import type { DefType } from "@/common"
import { useCurrentTenant } from "@/tenant"

type Props = {
  searchQuery: string
}

export default function TrashMenu({ searchQuery }: Props) {
  const { tenantSlug } = useCurrentTenant()
  const navigate = useNavigate()
  const { data: trashed } = useGetTrashedDefinitionsQuery()
  const [restoreDefinition] = useRestoreDefinitionMutation()
  const [menuOpen, setMenuOpen] = useState(false)
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar()

  if (!trashed?.length) {
    return null
  }

  const filtered = trashed.filter(({ name }) =>
    name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const handleMenuOpenChange = (doOpen: boolean) => {
    setMenuOpen(!sidebarOpen || doOpen)
    setSidebarOpen(true)
  }

  const handleRestore = async (id: string, type: DefType) => {
    await restoreDefinition(id).unwrap()
    await navigate({
      to: `/t/$tenantSlug/${type}s/$id` as const,
      params: { tenantSlug, id },
    })
  }

  return (
    <SidebarMenu>
      <Collapsible
        open={sidebarOpen && (menuOpen || !!searchQuery)}
        onOpenChange={handleMenuOpenChange}
        className="group/collapsible"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger
            render={
              <SidebarMenuButton>
                <HugeiconsIcon icon={Delete02Icon} />
                Trash
                {filtered.length > 0 && (
                  <div className="translate-y-px text-xs text-muted-foreground">
                    {filtered.length}
                  </div>
                )}
              </SidebarMenuButton>
            }
          />
          <CollapsibleContent>
            <ScrollArea className="**:data-[slot=scroll-area-viewport]:max-h-40">
              <SidebarMenuSub className="py-0">
                {filtered.map(({ id, name, type }) => (
                  <SidebarMenuSubItem key={id} className="group/trash-item">
                    <div className="flex items-center gap-1 py-1 pr-6 pl-2 text-sm text-sidebar-foreground">
                      <HugeiconsIcon
                        icon={CONFIG[type].icon}
                        className="shrink-0"
                        size={14}
                      />
                      {name}
                    </div>
                    <SidebarMenuAction
                      onClick={() => handleRestore(id, type)}
                      title="Restore"
                      className="top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover/trash-item:opacity-100"
                    >
                      <HugeiconsIcon icon={DeletePutBackIcon} />
                    </SidebarMenuAction>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </ScrollArea>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    </SidebarMenu>
  )
}
